import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { squeezoService } from '../services/squeezoService';
import type { SqueezoHistoryPoint } from '../types/squeezo';
import { useWebSocket } from '../contexts/WebSocketContext';
import { getSqueezoZoneColor } from '../utils/squeezoColors';
import { SqueezoSparkline } from './SqueezoSparkline';
import { SqueezoChartModal } from './SqueezoChartModal';

export const SqueezoGauge: React.FC = () => {
  const [currentValue, setCurrentValue] = useState<number | null>(null);
  const [isChartOpen, setIsChartOpen] = useState(false);
  const { subscribe } = useWebSocket();
  const queryClient = useQueryClient();

  // Fetch initial value (shares cache with SqueezoWidget)
  const { data } = useQuery({
    queryKey: ['squeezo', 'latest'],
    queryFn: () => squeezoService.getLatest(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Fetch sparkline history
  const { data: historyData } = useQuery({
    queryKey: ['squeezo', 'history'],
    queryFn: () => squeezoService.getHistory(20),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data?.data?.value !== undefined) {
      setCurrentValue(data.data.value);
    }
  }, [data]);

  // WebSocket subscription for real-time updates
  useEffect(() => {
    return subscribe('jdash.squeezo', (payload: unknown) => {
      const msg = payload as { data?: { value?: number; created_at?: string } };
      if (msg.data?.value !== undefined) {
        setCurrentValue(msg.data.value);

        // Prepend to sparkline history cache
        const newPoint: SqueezoHistoryPoint = {
          value: msg.data.value,
          created_at: msg.data.created_at ?? new Date().toISOString(),
        };
        queryClient.setQueryData(
          ['squeezo', 'history'],
          (old: { data: SqueezoHistoryPoint[] } | undefined) => {
            if (!old) return old;
            return { ...old, data: [newPoint, ...old.data.slice(0, 19)] };
          },
        );
      }
    });
  }, [subscribe, queryClient]);

  const [showTooltip, setShowTooltip] = useState(false);

  if (currentValue === null) return null;

  // One flat colour for the whole control — arc, needle, dot, value and
  // sparkline — keyed to the zone the number is in.
  const color = getSqueezoZoneColor(currentValue);
  const rounded = Math.round(currentValue);

  // Needle angle: 0 = left (bullish), 100 = right (bearish)
  const needleAngle = -90 + (currentValue / 100) * 180;

  const zoneLabel = rounded >= 65 ? 'Bearish' : rounded <= 45 ? 'Bullish' : 'Neutral';

  return (
    <>
      <div
        className="relative flex items-center gap-1 px-2 py-1 text-xs border rounded transition-colors bg-dark-bg border-dark-border cursor-pointer hover:border-gray-500"
        onClick={() => setIsChartOpen(true)}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {/* Hover tooltip legend */}
        {showTooltip && (
          <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 bg-dark-surface border border-dark-border rounded-md shadow-lg px-3 py-2 whitespace-nowrap pointer-events-none">
            <div className="text-[11px] font-semibold text-white mb-1.5">
              Squeezo: <span style={{ color }}>{rounded}</span> — {zoneLabel}
            </div>
            <div className="flex flex-col gap-0.5 text-[10px]">
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-bear-red" />
                <span className="text-gray-400">65-100</span>
                <span className="text-gray-300">Bearish</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-accent-yellow" />
                <span className="text-gray-400">45-65</span>
                <span className="text-gray-300">Neutral</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-bull-blue" />
                <span className="text-gray-400">0-45</span>
                <span className="text-gray-300">Bullish</span>
              </div>
            </div>
            <div className="text-[9px] text-gray-500 mt-1">Click for full chart</div>
          </div>
        )}
        {/* Mini gauge icon */}
        <svg width="16" height="12" viewBox="0 0 24 16" fill="none">
          {/* Semicircular arc */}
          <path
            d="M 2 14 A 10 10 0 0 1 22 14"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
          {/* Needle */}
          <line
            x1="12"
            y1="14"
            x2={12 + 7 * Math.cos((needleAngle * Math.PI) / 180)}
            y2={14 + 7 * Math.sin((needleAngle * Math.PI) / 180)}
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            className="transition-all duration-500"
          />
          {/* Center dot */}
          <circle cx="12" cy="14" r="1.5" fill={color} />
        </svg>
        {/* Value */}
        <span className="font-semibold tabular-nums" style={{ color }}>
          {rounded}
        </span>
        {/* Sparkline */}
        {historyData?.data && historyData.data.length >= 2 && (
          <div className="w-16">
            <SqueezoSparkline
              data={historyData.data}
              color={color}
              onClick={() => setIsChartOpen(true)}
            />
          </div>
        )}
      </div>

      {/* Chart modal */}
      {isChartOpen && (
        <SqueezoChartModal
          currentValue={currentValue}
          onClose={() => setIsChartOpen(false)}
        />
      )}
    </>
  );
};
